// Vista PDF de una factura de venta (servicios o especializada) EN CUALQUIER estado:
// abre una ventana A4 imprimible (el usuario la guarda como PDF desde el diálogo).
// · Autorizada: muestra serie, clave de acceso y nº de autorización.
// · Por emitir / borrador: marca de agua "BORRADOR — NO TRIBUTARIO" (no reemplaza al
//   RIDE oficial, que se genera al autorizar).
import { apiClient } from '@/lib/api/client';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const esc = (s: unknown) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const usd = (v: number) => (Number(v) || 0).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });

export interface FacturaPdfMeta {
  id: string;
  cliente?: string | null;
  ruc_receptor?: string | null;
  fecha_emision?: string | null;
  serie_comprobante?: string | null;
  clave_acceso?: string | null;
  numero_autorizacion?: string | null;
  estado_emision?: string | null;
}
interface LineaDet { codigo: string | null; descripcion: string; cantidad: number; precio_unitario: number; descuento: number; iva_tarifa: number }
interface ReembolsoRow { id: string; proveedor: string | null; ruc: string | null; serie: string | null; fecha: string | null; base: number; iva: number }

export async function abrirFacturaPdf(meta: FacturaPdfMeta): Promise<void> {
  // líneas + observaciones del comprobante
  const det = (await apiClient.post('/rpc/fac_obtener', { _ticket_id: meta.id }, FIN)).data as
    { lineas: LineaDet[]; observaciones: string | null; origen: string; estado_emision: string } | null;
  if (!det) throw new Error('Comprobante no encontrado');
  // reembolsos por cuenta de terceros asociados (si es factura por reembolso de gastos)
  const reemb = ((await apiClient.post('/rpc/fe_reembolso_de_factura', { _ticket_id: meta.id }, FIN)).data ?? []) as ReembolsoRow[];
  // empresa emisora (para la cabecera)
  const me = (await apiClient.post('/rpc/me', {})).data as { user?: { empresa?: { id?: string } } };
  const empId = me?.user?.empresa?.id;
  const emp = empId
    ? ((await apiClient.get(`/rest/autenticacion.empresas?id=eq.${empId}&select=nombre,ruc,direccion,telefono,logo`)).data?.[0] ?? {})
    : {};

  const autorizada = (meta.estado_emision ?? det.estado_emision) === 'autorizado' || !!meta.numero_autorizacion;
  // serie: la del comprobante o, si falta, derivada de la clave de acceso (estab+pto+secuencial)
  const serie = meta.serie_comprobante
    ?? (meta.clave_acceso && meta.clave_acceso.length === 49
      ? `${meta.clave_acceso.slice(24, 27)}-${meta.clave_acceso.slice(27, 30)}-${meta.clave_acceso.slice(30, 39)}`
      : null);
  const lineas = det.lineas ?? [];
  const base = (l: LineaDet) => Number(l.cantidad) * (Number(l.precio_unitario) - Number(l.descuento || 0));
  const subtotal = lineas.reduce((s, l) => s + base(l), 0);
  const descuento = lineas.reduce((s, l) => s + Number(l.cantidad) * Number(l.descuento || 0), 0);
  const iva = lineas.reduce((s, l) => s + base(l) * Number(l.iva_tarifa || 0) / 100, 0);
  const total = subtotal + iva;
  // Reembolso: base+IVA de terceros va aparte (igual que el XML/RIDE del SRI). El
  // VALOR TOTAL propio es subtotal+iva; el TOTAL A PAGAR suma el reembolso.
  const reembBase = reemb.reduce((s, r) => s + Number(r.base || 0), 0);
  const reembIva = reemb.reduce((s, r) => s + Number(r.iva || 0), 0);
  const reembTotal = reembBase + reembIva;
  const totalPagar = total + reembTotal;

  const filasReemb = reemb.map((r, i) => `
    <tr><td class="c">${i + 1}</td><td>${esc(r.proveedor || '—')}${r.ruc ? `<br><span class="mono" style="font-size:10px;color:#666">${esc(r.ruc)}</span>` : ''}</td>
    <td class="mono">${esc(r.serie || '—')}</td><td class="c">${esc(r.fecha ? String(r.fecha).slice(0, 10) : '—')}</td>
    <td class="r">${usd(Number(r.base))}</td><td class="r">${usd(Number(r.iva))}</td></tr>`).join('');

  const filas = lineas.map((l, i) => `
    <tr><td class="c">${i + 1}</td><td class="mono">${esc(l.codigo || '—')}</td><td>${esc(l.descripcion)}</td>
    <td class="r">${Number(l.cantidad).toLocaleString(LOCALE)}</td><td class="r">${usd(Number(l.precio_unitario))}</td>
    <td class="r">${Number(l.descuento) > 0 ? usd(Number(l.descuento) * Number(l.cantidad)) : '—'}</td>
    <td class="r">${Number(l.iva_tarifa)}%</td><td class="r">${usd(base(l) * (1 + Number(l.iva_tarifa || 0) / 100))}</td></tr>`).join('');

  const html = `<!doctype html><html><head><meta charset="utf-8">
  <title>${esc(det.origen === 'facturacion_especializada' ? 'Factura especializada' : 'Factura')} ${esc(serie ?? '(por emitir)')}</title>
  <style>
    @page { size: A4; margin: 16mm; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #222; position: relative; }
    h1 { font-size: 17px; margin: 0 0 2px; } h2 { font-size: 13px; margin: 10px 0 2px; color: #444; }
    .meta { margin: 2px 0; } .mono { font-family: monospace; }
    table { width: 100%; border-collapse: collapse; margin-top: 10px; }
    th, td { border: 1px solid #999; padding: 4px 6px; } th { background: #f0f0f0; text-align: left; font-size: 11px; }
    .r { text-align: right; } .c { text-align: center; } .tot td { font-weight: bold; }
    .caja { border: 1px solid #999; padding: 8px 10px; margin-top: 10px; }
    .marca { position: fixed; top: 40%; left: 8%; transform: rotate(-24deg); font-size: 52px; font-weight: 800;
             color: rgba(200, 30, 30, 0.15); letter-spacing: 4px; pointer-events: none; z-index: 99; }
    .logo { max-height: 60px; max-width: 180px; float: right; }
  </style></head><body>
  ${autorizada ? '' : '<div class="marca">BORRADOR · NO TRIBUTARIO</div>'}
  ${emp.logo ? `<img class="logo" src="${esc(emp.logo)}" alt=""/>` : ''}
  <h1>${esc(emp.nombre ?? '')}</h1>
  ${emp.ruc ? `<div class="meta">RUC: ${esc(emp.ruc)}</div>` : ''}
  ${emp.direccion ? `<div class="meta">${esc(emp.direccion)}</div>` : ''}
  ${emp.telefono ? `<div class="meta">Tel: ${esc(emp.telefono)}</div>` : ''}
  <h2>${det.origen === 'facturacion_especializada' ? 'FACTURA (ESPECIALIZADA)' : 'FACTURA'}
      ${esc(serie ?? '— POR EMITIR —')}</h2>
  <div class="meta"><b>Fecha de emisión:</b> ${esc(meta.fecha_emision ?? '')}</div>
  ${meta.clave_acceso ? `<div class="meta"><b>Clave de acceso:</b> <span class="mono">${esc(meta.clave_acceso)}</span></div>` : ''}
  ${meta.numero_autorizacion ? `<div class="meta"><b>Autorización:</b> <span class="mono">${esc(meta.numero_autorizacion)}</span></div>` : ''}
  <div class="meta"><b>Cliente:</b> ${esc(meta.cliente ?? '—')}${meta.ruc_receptor ? ` · ${esc(meta.ruc_receptor)}` : ''}</div>
  <table>
    <thead><tr><th>#</th><th>Código</th><th>Descripción</th><th class="r">Cant.</th><th class="r">P. unit.</th><th class="r">Desc.</th><th class="r">IVA</th><th class="r">Total</th></tr></thead>
    <tbody>${filas}</tbody>
    <tfoot>
      <tr><td colspan="7" class="r">Subtotal</td><td class="r">${usd(subtotal)}</td></tr>
      ${descuento > 0 ? `<tr><td colspan="7" class="r">Descuento</td><td class="r">−${usd(descuento)}</td></tr>` : ''}
      <tr><td colspan="7" class="r">IVA</td><td class="r">${usd(iva)}</td></tr>
      <tr class="tot"><td colspan="7" class="r">${reemb.length ? 'VALOR TOTAL' : 'TOTAL'}</td><td class="r">${usd(total)}</td></tr>
      ${reemb.length ? `<tr><td colspan="7" class="r">Total reembolso</td><td class="r">${usd(reembTotal)}</td></tr>
      <tr class="tot"><td colspan="7" class="r">TOTAL A PAGAR</td><td class="r">${usd(totalPagar)}</td></tr>` : ''}
    </tfoot>
  </table>
  ${reemb.length ? `<h2>Reembolso por cuenta de terceros (${reemb.length} comprobante${reemb.length === 1 ? '' : 's'})</h2>
  <table>
    <thead><tr><th>#</th><th>Proveedor</th><th>Comprobante</th><th class="c">Fecha</th><th class="r">Base imponible</th><th class="r">IVA</th></tr></thead>
    <tbody>${filasReemb}</tbody>
    <tfoot>
      <tr class="tot"><td colspan="4" class="r">Total base / IVA reembolso</td><td class="r">${usd(reembBase)}</td><td class="r">${usd(reembIva)}</td></tr>
      <tr class="tot"><td colspan="5" class="r">TOTAL REEMBOLSO</td><td class="r">${usd(reembTotal)}</td></tr>
    </tfoot>
  </table>` : ''}
  ${det.observaciones ? `<div class="caja"><b>Observaciones:</b> ${esc(det.observaciones)}</div>` : ''}
  ${autorizada ? '' : `<div class="caja" style="border-color:#c33;color:#a22"><b>Documento en borrador:</b>
    aún no ha sido emitido/autorizado por el SRI — no tiene validez tributaria. El RIDE oficial
    estará disponible al autorizarse.</div>`}
  </body></html>`;

  const win = window.open('', '_blank', 'width=900,height=720');
  if (!win) throw new Error('El navegador bloqueó la ventana emergente');
  win.document.write(html);
  win.document.close();
  setTimeout(() => { try { win.focus(); win.print(); } catch { /* sin impresora */ } }, 300);
}
